import React, { Component } from 'react';
import { RouteComponentProps, redirectTo } from '@reach/router';
import { Container } from 'bloomer/lib/layout/Container';
import { Box } from 'bloomer/lib/elements/Box';
import { Title } from 'bloomer/lib/elements/Title';
import api from '../util/api';
import Animal from '../Entities/Animal';
import Conditional from '../components/Conditional';
import LoadingSpinner from '../elements/LoadingSpinner';
import { Button } from 'bloomer/lib/elements/Button';
import EvaluationModal from '../components/EvaluationModal';
import { Column } from 'bloomer/lib/grid/Column';
import { Columns } from 'bloomer/lib/grid/Columns';
import { Icon } from 'bloomer/lib/elements/Icon';
import { confirmSuccessAfter, popupErrorFromServer } from '../util/helperFunctions';

interface PropTypes extends RouteComponentProps<{ room: string }> {}

const HasEnrichmentIcon = () => <Icon isSize="large" className="fa fa-check has-text-success fa-3x" />;

const DoesNotHaveEnrichmentIcon = () => <Icon isSize="large" className="fa fa-times has-text-danger fa-3x" />;
interface StateTypes {
  animals: Animal[];
  isLoading: boolean;
  modalAnimal: null | Animal;
  isModalOpen: boolean;
}

class EvaluationsSingleRoom extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    animals: [],
    isLoading: true,
    isModalOpen: false,
    modalAnimal: null,
  };

  async refresh() {
    try {
      this.setState({ isLoading: true });
      const { room } = this.props;
      if (!room) {
        return redirectTo('/evaluations');
      }
      const animalsProm = api.animals.get({
        active: true,
        location: room,
      });

      const evaluationsProm = api.evaluations.get({});

      const [animals, evaluations] = await Promise.all([animalsProm, evaluationsProm]);

      animals
        .sort((a, b) => {
          if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
          }
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
          }
          return 0;
        })
        .map(animal => animal.findAndRegisterEvaluations(evaluations));

      this.setState({
        animals,
        isLoading: false,
      });
    } catch (err) {
      popupErrorFromServer(err);
    }
  }

  componentDidMount() {
    this.refresh();
  }

  openModal = (animal: Animal) => () =>
    this.setState({
      isModalOpen: true,
      modalAnimal: animal,
    });

  closeModal = () =>
    this.setState({
      isModalOpen: false,
      modalAnimal: null,
    });

  handleEvaluationSubmission = async (evaluation: Evaluation) => {
    await confirmSuccessAfter(`Evaluation saved successfully!`, async () => await api.evaluations.post(evaluation));
    this.closeModal();
    this.refresh();
  };

  render() {
    const { isLoading, animals, modalAnimal, isModalOpen } = this.state;
    const { room } = this.props;

    return (
      <>
        <Container>
          <Box>
            <Title>Animal Evaluations - Room {room}</Title>
            <Conditional check={!isLoading} alt={<LoadingSpinner />}>
              <>
                {animals.map(animal => (
                  <Box key={`animal-evaluation-${animal.aNumber}`}>
                    <Columns isVCentered={true}>
                      <Column>
                        <p className="is-size-5">{animal.name}</p>
                      </Column>
                      <Column className="has-text-centered">
                        <p>Morning Evaluation</p>
                        <div>
                          <Conditional check={animal.hasMorningEvaluation()} alt={<DoesNotHaveEnrichmentIcon />}>
                            <HasEnrichmentIcon />
                          </Conditional>
                        </div>
                      </Column>
                      <Column className="has-text-centered">
                        <p>Evening Evaluation</p>
                        <div>
                          <Conditional check={animal.hasEveningEvaluation()} alt={<DoesNotHaveEnrichmentIcon />}>
                            <HasEnrichmentIcon />
                          </Conditional>
                        </div>
                      </Column>
                      <Column>
                        <Button isColor="primary" onClick={this.openModal(animal)}>
                          Evaluate
                        </Button>
                      </Column>
                    </Columns>
                  </Box>
                ))}
              </>
            </Conditional>
          </Box>
        </Container>
        <Conditional check={modalAnimal !== null && isModalOpen}>
          <EvaluationModal
            handleClose={this.closeModal}
            handleSubmit={this.handleEvaluationSubmission}
            animal={modalAnimal as Animal}
          />
        </Conditional>
      </>
    );
  }
}

export default EvaluationsSingleRoom;
